import React, { useState, useEffect, useMemo } from "react";
import Header from "../header/Header";
import SideBar from "../sidebar/SideBar";
import AdminHeader from "./AdminHeader";
import { Dropdown, Modal, Table, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import adminsApi from "../../lib/admins";
import ThreeDot from "../../icons/ThreeDot";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import axios from "axios";
import { api } from "../../config/api";
import { doc, getDoc } from "@firebase/firestore";
import { db } from "../../config/firebase";
import { toast } from "react-toastify";
import { sendInvitation } from "../../lib/sendEmail";
import Loader from "../shared/loader/loader";
import DashboardLayout from "../layout";
import { useMyContext } from "../../context/MyContext";
import { getAllAdminUsers } from "../../lib/user";
import EditAdminForm from "./EditAdminForm";

const obj = {
  ative: true,
  content: "Clients",
};

const Admins = () => {
  const queryClient = useQueryClient();
  const [delModal, setDelModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { user: loggedinUser } = useMyContext();
  const [editModal, setEditModal] = useState(false);
  const [selectId, setSelectId] = useState();
  const [user, SetUser] = useState();

  const { data, isLoading, refetch } = useQuery(
    ["all-admins"],
    async () => {
      const res = await getAllAdminUsers();
      return res;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000, // Cache for 30 seconds to reduce unnecessary refetches
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/${id}`);
    },
    {
      onSuccess: () => {
        message.success("Admin deleted successfully!");
        setDelModal(false);
        queryClient.invalidateQueries(["all-admins"]);
      },
      onError: (error) => {
        message.error("Failed to delete admin: " + (error.response?.data?.message || error.message));
      }
    }
  );

  const SendMail = async (id) => {
    if (!id) return;
    const toastId = toast.loading("Sending invitation...");
    try {
      const res = await sendInvitation(id);
      toast.update(toastId, {
        render: res?.message || "Invitation sent successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error sending invitation:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to send invitation";
      toast.update(toastId, {
        render: msg,
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    }
  };

  const handleEdit = (admin) => {
    if (loggedinUser?.role === "super admin") {
      setEditingAdmin({ ...admin });
      setEditModal(true);
    } else {
      message.error("Only super admin can edit admins");
    }
  };

  // Function to check if user is a super admin - to hide delete option
  const isSuperAdmin = (role) => {
    return role === "super admin";
  };

  // Initialize filteredData when data changes
  useEffect(() => {
    if (data && Array.isArray(data)) {
      setFilteredData(data);
    }
  }, [data]);

  // Reset to first page when filteredData changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredData]);

  const columns = useMemo(
    () => [
      {
        title: "No.",
        render: (_, record, index) => (currentPage - 1) * pageSize + index + 1,
        width: 70,
        fixed: 'left',
      },
      {
        title: "Name",
        dataIndex: "name",
        ellipsis: true,
        width: 180,
      },
      {
        title: "Email",
        dataIndex: "email",
        ellipsis: true,
        width: 220,
      },
      {
        title: "Role",
        dataIndex: "role",
        width: 150,
      },
      {
        title: "Actions",
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                ...(loggedinUser?.role === "super admin"
                  ? [
                    {
                      key: "1",
                      label: "Edit",
                      onClick: () => handleEdit(record),
                    },
                  ]
                  : []),
                {
                  key: "2",
                  label: "Send Invitation",
                  onClick: () => SendMail(record.id),
                },
                ...(!isSuperAdmin(record.role)
                  ? [
                    {
                      key: "3",
                      label: "Delete",
                      onClick: () => {
                        Modal.confirm({
                          title: "Confirm",
                          icon: <ExclamationCircleOutlined />,
                          content: "Are you sure you want to delete this admin?",
                          okText: "Delete",
                          cancelText: "Cancel",
                          okType: "danger",
                          onOk: () => {
                            deleteMutation.mutate(record?.id);
                          },
                        });
                      },
                    },
                  ]
                  : []),
              ].filter((item) => item),
            }}
            placement="bottomRight"
          >
            <div className="border-2 w-fit rounded-md p-1 cursor-pointer hover:bg-gray-50 transition-colors">
              <BiDotsVerticalRounded size={20} />
            </div>
          </Dropdown>
        ),
        width: 100,
        fixed: 'right',
      },
    ],
    [loggedinUser?.role, currentPage, pageSize, deleteMutation]
  );

  // Add unique keys to data for better rendering
  const dataWithKeys = useMemo(() => {
    return filteredData.map((item, index) => ({
      ...item,
      key: item.id || index,
    }));
  }, [filteredData]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="w-full p-5">
          <Loader />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout>
        <div className="w-full">
          <Header title="Admins" />

          <div className="p-5 bg-[#eaeff6]">
            <AdminHeader
              filteredData={filteredData}
              setFilteredData={setFilteredData}
              Data={data}
            />
          </div>
          <div className="p-5">
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={dataWithKeys}
                size="middle"
                className="admin-table"
                loading={isLoading || deleteMutation.isLoading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                  },
                  total: filteredData.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} admins`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                scroll={{ x: 800 }}
                sticky
                bordered
              />
            </div>
          </div>
        </div>
      </DashboardLayout>

      <Modal
        title="Edit Admin"
        open={editModal}
        onCancel={() => {
          setEditModal(false);
          setEditingAdmin(null);
        }}
        footer={null}
        destroyOnClose
      >
        {editingAdmin && (
          <EditAdminForm
            key={editingAdmin.id}
            admin={editingAdmin}
            onClose={() => {
              setEditModal(false);
              setEditingAdmin(null);
            }}
            onSuccess={() => {
              setEditModal(false);
              setEditingAdmin(null);
              queryClient.invalidateQueries(["all-admins"]);
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default Admins;